'use client';

import {
  useState,
} from 'react';
import queryString from 'query-string';

import { camelizeKeys } from 'humps';

import {
  WagApiEndpoint,
  WAG_API_URL,
  WalkerProfile,
  WalkerProfileV6Endpoints,
} from '../../constants';

import {
  reviewsSchema,
  ReviewsSchema,
} from '../../utils/validation-schemas/walkerProfile/reviews';

import { V6ReviewsQueryParams, WalkerProfileReviewResponse, WalkerReviews } from '../../types/api';
import { tryCatch } from '../../utils';
import { applyPathParams } from '../../wag-react/common/api/utils/applyPathParams';
import { httpGet } from '../../wag-react/common/api/api';

type QueryParams = {
  limit?: number;
  offset?: number;
};

type PropsV6 = {
  identifier: string;
  queryParams?: V6ReviewsQueryParams;
}

const allServiceTypes: V6ReviewsQueryParams['serviceTypes'] = [
  'boarding', 'sitting',
  'walk_deluxe', 'walk_express', 'walk',
  'drop_in_30_min', 'drop_in_deluxe', 'drop_in',
  'daycare', 'training', 'training_digital', 'vet_chat',
  'custom_service', 'meet_and_greet_plus_walk', 'meet_and_greet',
];

type Props = {
  identifier: string;
  queryParams?: QueryParams;
}
// non-hook version
export const getWalkerProfileReviews = async ({
  identifier,
  queryParams = {
    limit: WalkerProfile.ReviewsPerPage,
    offset: 0,
  },
}: Props) => {
  const reviewUrl = applyPathParams(WagApiEndpoint.GetWalkerProfileReviewsByPromoCode)({
    promoCode: identifier,
  });
  const {
    data: reviewResponseData,
  } = await httpGet<WalkerProfileReviewResponse>(reviewUrl, {
    baseURL: WAG_API_URL,
    params: {
      limit: (queryParams as QueryParams).limit,
      offset: (queryParams as QueryParams).offset,
    },
  });
  const data = camelizeKeys(reviewResponseData) as WalkerProfileReviewResponse;
  const reviews = reviewsSchema.cast(data);

  return reviews;
};

export const getWalkerProfileReviewsV6 = async ({
  identifier,
  queryParams,
}: PropsV6) => {
  const reviewUrl = applyPathParams(WalkerProfileV6Endpoints.GetWalkerReviews)({
    identifier,
  });

  const params = {
    ...queryParams,
    reviewType: 'service',
  } as V6ReviewsQueryParams;

  const sortBy = [{
    column: 'createdTime',
    dir: 'desc',
  }];
  const stringifiedQueryParams = queryString.stringify(params, { arrayFormat: 'bracket' });

  const stringifiedSortBy = sortBy.map(
    (obj, i) => Object.keys(obj).map((key) => `sortBy[${i}][${key}]=${obj[key]}`).join('&')).join('&');

  const url = `${reviewUrl}?${stringifiedQueryParams}&${stringifiedSortBy}`;

  const [response, err] = await tryCatch(() => httpGet<WalkerReviews>(url, {
    baseURL: WAG_API_URL,
  }));

  if (err) {
    return null;
  }

  return response.data;
};

export const getWalkerReviewsV6 = async ({
  identifier,
  queryParams,
}: PropsV6) => {
  const reviews = await getWalkerProfileReviewsV6({
    identifier,
    queryParams,
  });

  if (reviews.items.length !== 0) {
    return reviews;
  }

  const adjustedReviews = await getWalkerProfileReviewsV6({
    identifier,
    queryParams: {
      ...queryParams,
      serviceTypes: allServiceTypes,
    },
  });

  return adjustedReviews;
};

export const useGetWalkerProfileReviewsV6 = (identifier: string) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState<WalkerReviews>({
    items: [],
    page: 1,
    perPage: 10,
    lastPage: 1,
    total: 0,
  });

  const getReviews = async (queryParams: V6ReviewsQueryParams) => {
    setIsLoading(true);

    try {
      const reviews = await getWalkerProfileReviewsV6({
        identifier,
        queryParams,
      });
      setIsLoading(false);
      setData(reviews as WalkerReviews);

      return reviews as WalkerReviews;
    } catch (_error) {
      setError(_error);
      setIsLoading(false);
      return _error;
    }
  };

  return {
    getReviews,
    isLoading,
    error,
    data,
  };
};

// hook version
export const useGetWalkerProfileReviews = (promoCode: string) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState<ReviewsSchema>(reviewsSchema.cast());

  const execute = async (queryParams: QueryParams) => {
    setIsLoading(true);

    try {
      const reviews = await getWalkerProfileReviews({
        identifier: promoCode,
        queryParams,
      });
      setIsLoading(false);
      setData(reviews as ReviewsSchema);

      return reviews;
    } catch (_error) {
      setError(_error);
      setIsLoading(false);
      return _error;
    }
  };

  return {
    execute,
    isLoading,
    error,
    data,
  };
};
